import api from '../utils/api';
export type ImportStatus =
  | 'draft'
  | 'queued'
  | 'running'
  | 'waiting'
  | 'complete'
  | 'partial'
  | 'failed';
export interface ImportJob {
  id: string;
  symbol: string;
  status: ImportStatus;
  stage: string;
  reason: string | null;
  attempts: number;
  readAt: string | null;
  updatedAt: string;
  nextRunAt: string;
  expiresAt: string;
  summary: Record<string, number> | null;
  plan: {
    instrument: {
      symbol: string;
      providerSymbol: string;
      name: string;
      exchange: string;
      currency: string;
      isin: string | null;
    };
    maximumCallUnitsPerAttempt: number;
    onboarding: {
      request: { fromDate: string; toDate: string };
      entities: { stockExists: boolean };
    };
  };
}
const requestOptions = { timeout: 10000 };
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isText = (value: unknown): value is string => typeof value === 'string';
const isNullableText = (value: unknown) => value === null || isText(value);
const isCount = (value: unknown) => Number.isSafeInteger(value) && Number(value) >= 0;
function parseJob(value: unknown): ImportJob {
  if (!isRecord(value)) throw new Error('Invalid import response');
  const plan = value.plan;
  const instrument = isRecord(plan) ? plan.instrument : undefined;
  const onboarding = isRecord(plan) ? plan.onboarding : undefined;
  if (
    !['id', 'symbol', 'stage', 'updatedAt', 'nextRunAt', 'expiresAt'].every((key) =>
      isText(value[key]),
    ) ||
    !['draft', 'queued', 'running', 'waiting', 'complete', 'partial', 'failed'].includes(
      String(value.status),
    ) ||
    !isCount(value.attempts) ||
    !isNullableText(value.readAt) ||
    !isNullableText(value.reason) ||
    !(
      value.summary === null ||
      (isRecord(value.summary) &&
        [
          'dailyObservations',
          'rejectedObservations',
          'indicatorsCompleted',
          'signalsGenerated',
          'incompletePeriods',
          'insufficientHistory',
          'failures',
        ].every((key) => isCount((value.summary as Record<string, unknown>)[key])))
    ) ||
    !isRecord(plan) ||
    !isCount(plan.maximumCallUnitsPerAttempt) ||
    !isRecord(instrument) ||
    !['symbol', 'providerSymbol', 'name', 'exchange', 'currency'].every((key) =>
      isText(instrument[key]),
    ) ||
    !isNullableText(instrument.isin) ||
    !isRecord(onboarding) ||
    !isRecord(onboarding.request) ||
    !isText(onboarding.request.fromDate) ||
    !isText(onboarding.request.toDate) ||
    !isRecord(onboarding.entities) ||
    typeof onboarding.entities.stockExists !== 'boolean'
  )
    throw new Error('Invalid import response');
  return value as unknown as ImportJob;
}
function parseList(value: unknown, key: string): ImportJob[] {
  if (!isRecord(value) || !Array.isArray(value[key])) throw new Error('Invalid import response');
  return (value[key] as unknown[]).map(parseJob);
}
export async function searchImports(query: string): Promise<ImportJob[]> {
  const { data } = await api.get<unknown>('/jwt/onboarding/search', {
    ...requestOptions,
    params: { query },
  });
  return parseList(data, 'plans');
}
export async function confirmImport(id: string): Promise<ImportJob> {
  const { data } = await api.post<unknown>(
    `/jwt/onboarding/${encodeURIComponent(id)}/confirm`,
    undefined,
    requestOptions,
  );
  return parseJob(data);
}
export async function listImports(): Promise<ImportJob[]> {
  const { data } = await api.get<unknown>('/jwt/onboarding/jobs', requestOptions);
  return parseList(data, 'jobs');
}
export async function readImport(id: string): Promise<ImportJob> {
  const { data } = await api.post<unknown>(
    `/jwt/onboarding/${encodeURIComponent(id)}/read`,
    undefined,
    requestOptions,
  );
  return parseJob(data);
}
